<template>
  <template v-if="isLoading">
    <div class="w-full h-full flex flex-row justify-center items-center">
      <LoadingSpinner></LoadingSpinner>
    </div>
  </template>
  <template v-else>
    <div class="completionist-widget w-full h-full flex flex-col">
      <div class="flex flex-col gap-4">
        <label 
          class="text-center text-12px text-challenge-blue font-bold uppercase" 
          v-html="stats_label"
        ></label>
        <div class="relative overflow-hidden">
          <Transition name="slide" mode="out-in">
            <div 
              v-if="statsMode === 'default'"
              class="grid grid-cols-2 px-x2"
            >
              <div 
                v-for="(stat, stat_index) in stats" 
                :key="stat_index"
                class="flex flex-col gap-x1 items-center justify-center p-4 font-medium text-center"
                :class="{    
                  'border-l-1 border-challenge-blue': stat_index % 2 === 1,
                  'border-t-1 border-challenge-blue': stat_index > 1
                }"
              >
                <p class="text-12px laptop:text-14px" v-html="stat.title"></p>
                <p class="text-24px laptop:text-32px" v-html="stat.value"></p>
              </div>
            </div>
            <div 
              v-else-if="statsMode === 'toolbar'"
              class="flex flex-row justify-around"
            >
              <div 
                v-for="(stat, stat_index) in stats" 
                :key="stat_index"
                class="w-full flex flex-row gap-x1 items-center justify-center p-2 font-medium text-center text-12px laptop:text-14px"
                :class="{    
                  'border-l-1 border-challenge-blue': stat_index > 0
                }"
              >
                <p v-html="stat.title"></p>:
                <p v-html="stat.value"></p>
              </div>
            </div>
          </Transition>
        </div>
      </div>
      <div class="flex-1 flex flex-col gap-4 mt-8">
        <label 
          class="text-center text-12px text-challenge-blue font-bold uppercase" 
          v-html="activities_label"
        ></label>
        <div class="relative flex-1">
          <div 
            ref="activitiesWrapEl"
            class="absolute inset-0 overflow-y-auto"
          >
            <div class="w-full h-full">
              <div class="flex flex-col gap-3">
                <CompletionistCard 
                  v-for="(activity, activity_index) in activities" 
                  :key="activity_index" 
                  :case-number="activity_index + 1"
                  :data="activity"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup>
import { computed, ref, onMounted, defineProps, defineExpose } from 'vue'
import { LoadingSpinner, CompletionistCard } from '@/components/'

// Props
const props = defineProps({
  jobnum: {
    type: String,
    required: true
  },
  mode: {
    type: String,
    required: true
  },
  token: {
    type: String,
    default: null
  }
})

// Variables
const isLoading = ref(true)
const data = ref(null)

// DOM
const activitiesWrapEl = ref(null)

// Variables
const statsMode = ref('default')

// Computed
const stats_label = computed(() => data.value?.stats_label)
const activities_label = computed(() => data.value?.activities_label)
const stats = computed(() => data.value?.stats)
const activities = computed(() => data.value?.challenges || data.value?.activities)

// Methods
const loadData = async (jobnum) => {
  const url = `${ process.env.VUE_APP_COMPLETIONIST_API_URL }/${ jobnum }?mode=${ props.mode || 'case_challenge' }&token=${ props.token }`
  const req = await fetch(url)
  const res = await req.json()
  data.value = res
}

const handleListScroll = (event) => {
  const scrollTop = event.target.scrollTop
  if (scrollTop >= 100 && statsMode.value === 'default') statsMode.value = 'toolbar'
  else if ((scrollTop < 100 && statsMode.value === 'toolbar')) statsMode.value = 'default'
}

// Life Cycle Hooks
onMounted(async () => {
  // activitiesWrapEl.value.addEventListener('scroll', handleListScroll)
  isLoading.value = true
  await loadData(props.jobnum)
  isLoading.value = false
})

defineExpose({
  loadData
})
</script>

<style src="@/assets/css/tailwind.css"></style>
<style src="@/assets/css/_fontface.css"></style>
<style src="@/assets/css/_variables.css"></style>
<style src="@/assets/css/_global.css"></style>
<style src="@/assets/css/_animations.css"></style>
<style src="@/assets/css/main.css"></style>

<style scoped>
.completionist-widget {
  font-family: var(--font-family);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>